"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";
import {
  parseVoiceCommand,
  getVoicePageContext,
  type VoiceAction,
} from "@/services/voiceActions";

const MAX_NETWORK_RETRIES = 8;

export interface VoiceIntentResult {
  action: VoiceAction;
  transcript: string;
}

/**
 * Dictée continue jusqu’à arrêt → analyse /api/ai/voice-command (intentions CRM : prospect, devis, navigation…).
 */
export function useVoiceIntentDictation(
  pathname: string | null | undefined,
  onParsed: (result: VoiceIntentResult) => void
) {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef<any>(null);
  const finalTranscriptRef = useRef("");
  const onParsedRef = useRef(onParsed);
  /** true tant que l’utilisateur n’a pas recliqué pour arrêter (évite que onend coupe l’écoute). */
  const sessionActiveRef = useRef(false);
  /** Erreurs « network » consécutives (service Google / connexion). */
  const networkRetryCountRef = useRef(0);
  /** Délai avant recognition.start() dans onend (ms), après erreur réseau. */
  const restartDelayMsRef = useRef(0);

  useEffect(() => {
    onParsedRef.current = onParsed;
  }, [onParsed]);

  const start = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("La reconnaissance vocale n'est pas supportée. Utilisez Chrome ou Edge.");
      return;
    }

    sessionActiveRef.current = true;
    networkRetryCountRef.current = 0;
    restartDelayMsRef.current = 0;

    const recognition = new SpeechRecognition();
    recognition.lang = "fr-FR";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          networkRetryCountRef.current = 0;
          finalTranscriptRef.current += r[0].transcript;
        } else {
          interim += r[0].transcript;
        }
      }
      setTranscript(finalTranscriptRef.current + interim);
    };

    recognition.onerror = (event: any) => {
      const code = event.error as string;

      if (code === "no-speech" && sessionActiveRef.current) {
        return;
      }
      if (code === "aborted" && !sessionActiveRef.current) {
        return;
      }

      // « network » : coupure vers le service de reconnaissance (Google) — souvent temporaire.
      if (code === "network" && sessionActiveRef.current) {
        networkRetryCountRef.current += 1;
        if (networkRetryCountRef.current > MAX_NETWORK_RETRIES) {
          sessionActiveRef.current = false;
          setIsListening(false);
          networkRetryCountRef.current = 0;
          toast.error(
            "Reconnaissance vocale indisponible (réseau). Vérifiez la connexion et réessayez."
          );
          return;
        }
        restartDelayMsRef.current = Math.min(
          400 + networkRetryCountRef.current * 250,
          3000
        );
        if (typeof console !== "undefined" && console.warn) {
          console.warn(
            "[voice-intent] network error, retry",
            networkRetryCountRef.current,
            "/",
            MAX_NETWORK_RETRIES
          );
        }
        return;
      }

      if (typeof console !== "undefined" && console.warn) {
        console.warn("[voice-intent] speech error:", code);
      }
      sessionActiveRef.current = false;
      networkRetryCountRef.current = 0;
      setIsListening(false);
    };

    // Chrome termine souvent la session après une pause ; on relance tant que l’utilisateur n’a pas arrêté.
    recognition.onend = () => {
      if (!sessionActiveRef.current) {
        setIsListening(false);
        return;
      }
      const delay = restartDelayMsRef.current;
      restartDelayMsRef.current = 0;
      const restart = () => {
        if (!sessionActiveRef.current) {
          setIsListening(false);
          return;
        }
        try {
          recognition.start();
        } catch {
          sessionActiveRef.current = false;
          networkRetryCountRef.current = 0;
          setIsListening(false);
        }
      };
      if (delay > 0) {
        window.setTimeout(restart, delay);
      } else {
        restart();
      }
    };

    recognitionRef.current = recognition;
    finalTranscriptRef.current = "";
    setTranscript("");
    try {
      recognition.start();
      setIsListening(true);
    } catch {
      sessionActiveRef.current = false;
      setIsListening(false);
    }
  }, []);

  const stop = useCallback(async () => {
    sessionActiveRef.current = false;
    networkRetryCountRef.current = 0;
    restartDelayMsRef.current = 0;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    setIsListening(false);

    const text = finalTranscriptRef.current.trim() || transcript.trim();
    if (!text) {
      setTranscript("");
      return;
    }

    setIsProcessing(true);
    try {
      const ctx = getVoicePageContext(pathname);
      const action = await parseVoiceCommand(text, ctx);
      onParsedRef.current({ action, transcript: text });
    } catch (e) {
      console.warn("Voice intent error:", e);
    } finally {
      setIsProcessing(false);
      setTranscript("");
    }
  }, [transcript, pathname]);

  return { isListening, isProcessing, transcript, start, stop };
}
